import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, GraduationCap, User, Briefcase,
  FileText, CheckCircle2, Send, Loader2, ClipboardCheck, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getAllPrograms, PROGRAM_CATEGORIES } from "@/data/programCatalog";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, key: "personal",   label: "Personal",   icon: User },
  { id: 2, key: "background", label: "Background", icon: Briefcase },
  { id: 3, key: "motivation", label: "Motivation", icon: FileText },
  { id: 4, key: "review",     label: "Review",     icon: ClipboardCheck },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ProgramApplication() {
  const [searchParams] = useSearchParams();
  const preselectedProgram = searchParams.get("program") || "";

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    program: preselectedProgram,
    background: "",
    motivation: "",
  });

  const programs = getAllPrograms();
  const programsByCategory = programs.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});
  const selectedProgram = programs.find((p) => p.id === formData.program);

  const handleChange = (e) => {
    setFormData((d) => ({ ...d, [e.target.name]: e.target.value }));
    setErrors((er) => ({ ...er, [e.target.name]: undefined }));
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 1) {
      if (!formData.name.trim()) e.name = "Required";
      if (!formData.email.trim()) e.email = "Required";
      else if (!EMAIL_RE.test(formData.email)) e.email = "Enter a valid email";
    }
    if (s === 2) {
      if (!formData.program) e.program = "Choose a program";
      if (!formData.background.trim() || formData.background.trim().length < 30)
        e.background = "Please share at least a brief professional background (≥ 30 characters)";
    }
    if (s === 3) {
      if (!formData.motivation.trim() || formData.motivation.trim().length < 30)
        e.motivation = "Please describe your motivation (≥ 30 characters)";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep(step)) setStep((s) => Math.min(s + 1, STEPS.length)); };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
      toast.error("Please complete all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          submittedAt: new Date().toISOString(),
          status: "pending_review",
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSubmittedId(data.id);
        setSubmitted(true);
        toast.success("Application submitted!");
      } else {
        toast.error(data.detail || "Submission failed. Please try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------- Success state ----------------
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="application-success">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold mb-3" style={{ color: "#991B1B" }}>
              Application Received
            </h1>
            <p className="text-lg text-[#1E3A8A] mb-2">
              Thank you, {formData.name.split(" ")[0]}. A confirmation email is on its way.
            </p>
            {submittedId && (
              <p className="text-sm text-[#1E3A8A]/60 mb-8 font-mono">Ref: {submittedId}</p>
            )}
            <Card className="border-[#E5E7EB] bg-white text-left mb-8">
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: "#991B1B" }}>What's Next?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { step: "1", title: "Application Review", desc: "Admissions reviews your credentials" },
                    { step: "2", title: "Interview Invitation", desc: "Qualified candidates receive an invite" },
                    { step: "3", title: "Enrollment Decision", desc: "Final decision within 2–3 weeks" },
                    { step: "4", title: "Portal Access", desc: "Begin your learning journey" },
                  ].map((it) => (
                    <div key={it.step} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center flex-shrink-0">
                        <span className="font-mono font-bold text-sm text-[#1E3A8A]">{it.step}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-[#1E3A8A]">{it.title}</p>
                        <p className="text-sm text-[#1E3A8A]/60">{it.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/programs"><Button variant="outline" className="border-[#E5E7EB]">Browse Programs</Button></Link>
              <Link to="/"><Button className="bg-[#1E3A8A] text-white hover:bg-[#1E40AF]">Return Home</Button></Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // ---------------- Wizard ----------------
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="application-page">
      {/* Hero */}
      <section className="bg-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <Link to="/programs" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Programs
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <GraduationCap className="w-7 h-7" />
            <span className="text-sm text-white/70">Program Application</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            Apply to Zodiac Institute
          </h1>
          <p className="text-base text-white/80 mt-2 max-w-2xl">
            A short, structured application. Save your progress in 3 minutes.
          </p>
        </div>
      </section>

      {/* Progress indicator */}
      <section className="bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <ol className="flex items-center justify-between gap-2">
            {STEPS.map((s, idx) => {
              const done = s.id < step;
              const active = s.id === step;
              const Icon = s.icon;
              return (
                <li key={s.key} className="flex items-center flex-1 last:flex-none gap-2 min-w-0">
                  <div
                    className={`flex items-center justify-center w-9 h-9 rounded-full border-2 transition-colors flex-shrink-0
                      ${done ? "bg-emerald-500 border-emerald-500 text-white"
                        : active ? "bg-[#1E3A8A] border-[#1E3A8A] text-white"
                        : "bg-white border-[#E5E7EB] text-[#1E3A8A]/40"}`}
                    data-testid={`step-indicator-${s.key}`}
                  >
                    {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className={`text-xs sm:text-sm font-medium truncate ${active ? "text-[#1E3A8A]" : "text-[#1E3A8A]/50"}`}>
                    {s.label}
                  </span>
                  {idx < STEPS.length - 1 && (
                    <div className={`h-0.5 flex-1 mx-1 ${done ? "bg-emerald-500" : "bg-[#E5E7EB]"}`} />
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Wizard panel */}
      <section className="py-10 lg:py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-[#E5E7EB] bg-white shadow-lg">
            <CardContent className="p-6 sm:p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                >
                  {step === 1 && (
                    <div className="space-y-5">
                      <header>
                        <h2 className="font-heading text-2xl font-bold" style={{ color: "#991B1B" }}>Personal Information</h2>
                        <p className="text-sm text-[#1E3A8A]/70 mt-1">Who we'll be in touch with.</p>
                      </header>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field id="name" label="Full Name *" error={errors.name}>
                          <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" className="border-[#E5E7EB]" data-testid="name-input"/>
                        </Field>
                        <Field id="email" label="Email Address *" error={errors.email}>
                          <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" className="border-[#E5E7EB]" data-testid="email-input"/>
                        </Field>
                      </div>
                      <Field id="phone" label="Phone Number">
                        <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="+1 (555) 123-4567" className="border-[#E5E7EB]" data-testid="phone-input"/>
                      </Field>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-5">
                      <header>
                        <h2 className="font-heading text-2xl font-bold" style={{ color: "#991B1B" }}>Program & Background</h2>
                        <p className="text-sm text-[#1E3A8A]/70 mt-1">Choose your program and tell us about your experience.</p>
                      </header>
                      <Field id="program" label="Program of Interest *" error={errors.program}>
                        <select id="program" name="program" value={formData.program} onChange={handleChange}
                          className="w-full h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/20"
                          data-testid="program-select">
                          <option value="">Select a program…</option>
                          {Object.entries(programsByCategory).map(([catId, progs]) => (
                            <optgroup key={catId} label={PROGRAM_CATEGORIES[catId]?.title || catId}>
                              {progs.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                            </optgroup>
                          ))}
                        </select>
                      </Field>
                      <Field id="background" label="Professional Background *" error={errors.background} hint={`${formData.background.length}/2000`}>
                        <Textarea id="background" name="background" value={formData.background} onChange={handleChange} rows={5} maxLength={2000} placeholder="Describe your professional background, current role, and relevant experience…" className="border-[#E5E7EB] resize-none" data-testid="background-input"/>
                      </Field>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-5">
                      <header>
                        <h2 className="font-heading text-2xl font-bold" style={{ color: "#991B1B" }}>Motivation</h2>
                        <p className="text-sm text-[#1E3A8A]/70 mt-1">Your goals and why this program is the right fit.</p>
                      </header>
                      <Field id="motivation" label="Why this program? *" error={errors.motivation} hint={`${formData.motivation.length}/2000`}>
                        <Textarea id="motivation" name="motivation" value={formData.motivation} onChange={handleChange} rows={7} maxLength={2000} placeholder="Share your goals, what you hope to achieve, and how this program aligns with your career aspirations…" className="border-[#E5E7EB] resize-none" data-testid="motivation-input"/>
                      </Field>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="space-y-5" data-testid="review-step">
                      <header>
                        <h2 className="font-heading text-2xl font-bold" style={{ color: "#991B1B" }}>Review & Submit</h2>
                        <p className="text-sm text-[#1E3A8A]/70 mt-1">Confirm everything is correct before submitting.</p>
                      </header>
                      <div className="rounded-lg border border-[#E5E7EB] bg-[#F8FAFC] divide-y divide-[#E5E7EB]">
                        <Row label="Name" value={formData.name} />
                        <Row label="Email" value={formData.email} />
                        <Row label="Phone" value={formData.phone || "—"} />
                        <Row label="Program" value={selectedProgram?.title || formData.program} />
                        <Row label="Background" value={formData.background} multiline />
                        <Row label="Motivation" value={formData.motivation} multiline />
                      </div>
                      <p className="text-xs text-[#1E3A8A]/60">
                        By submitting you agree to be contacted by Zodiac Workforce Intelligence Institute regarding admissions.
                      </p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Footer nav */}
              <div className="flex items-center justify-between mt-8 pt-5 border-t border-[#E5E7EB]">
                <Button
                  variant="ghost"
                  onClick={back}
                  disabled={step === 1 || submitting}
                  className="text-[#1E3A8A]"
                  data-testid="wizard-back"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                {step < STEPS.length ? (
                  <Button
                    onClick={next}
                    className="bg-[#1E3A8A] text-white hover:bg-[#1E40AF]"
                    data-testid="wizard-next"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={submit}
                    disabled={submitting}
                    className="bg-[#991B1B] text-white hover:bg-[#7F1D1D]"
                    data-testid="wizard-submit"
                  >
                    {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Submitting…</>) : (<><Send className="w-4 h-4 mr-2"/>Submit Application</>)}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}

function Field({ id, label, error, hint, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{label}</Label>
        {hint && <span className="text-xs text-[#1E3A8A]/40">{hint}</span>}
      </div>
      {children}
      {error && <p className="text-xs text-red-600" data-testid={`${id}-error`}>{error}</p>}
    </div>
  );
}

function Row({ label, value, multiline }) {
  return (
    <div className="px-4 py-3 grid grid-cols-[140px_1fr] gap-4 text-sm">
      <div className="text-[#1E3A8A]/60 font-medium">{label}</div>
      <div className={`text-[#1E3A8A] ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>{value}</div>
    </div>
  );
}
